import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccessToken } from '../useAccessToken'
import { useCallback } from 'react'
import useProjectId from '../../customHooks/useProjectId'
import { SheetsDto } from '../../interfaces/editing/dtos/SheetsDto'
import { useEditingContext } from '../../context/EditingContext'
import { createEditsDto } from '../../utils/createEditsDto'
import { FetchResponse, makeResultFetch } from '../genericFetch'
import { apiRequestParams } from '../fetchOptions'

export const useSaveSheets = () => {
  const getAccessToken = useAccessToken()
  const projectId = useProjectId()
  const { edits, setEdits } = useEditingContext()
  const queryClient = useQueryClient()

  const mutationFunction = useCallback(async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const editsDto = createEditsDto(edits)

      const sheetsDto: SheetsDto = {
        columns: [...editsDto.columns],
        budgets: [...editsDto.budgets],
        comments: [...editsDto.comments],
      }

      const fetchedData = makeResultFetch<boolean>(
        `/Sheet/${projectId}/Save`,
        apiRequestParams<SheetsDto>(accessToken, 'PUT', sheetsDto)
      )

      return fetchedData
    })
  }, [edits, getAccessToken, projectId])

  // optional data variable
  const handleSuccess = (response: FetchResponse<boolean>) => {
    if (response.success) {
      setEdits((prevState) => {
        return {
          ...prevState,
          sheets: {},
        }
      })

      queryClient.invalidateQueries({ queryKey: ['cashflow', projectId] })
      queryClient.invalidateQueries({
        queryKey: ['cashflowNotices', projectId],
      })
      queryClient.invalidateQueries({ queryKey: ['monthColumns', projectId] })

      queryClient.invalidateQueries({ queryKey: ['performance', projectId] })
      queryClient.invalidateQueries({
        queryKey: ['performanceNotices', projectId],
      })
      queryClient.invalidateQueries({
        queryKey: ['performanceTotals', projectId],
      })
    }

    return response
  }

  const saveSheets = useMutation({
    mutationFn: mutationFunction,
    onSuccess: handleSuccess,
    onError: handleSuccess,
  })

  return saveSheets
}
